import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import { Chip } from "@/components/ui-bits";
import {
    Upload, FileText, Loader2, Trash2, Save, Mail, Download, Pencil,
    Layers, GitCompare, X, ClipboardCopy, MessageSquare, PenLine,
} from "lucide-react";
import { toast } from "sonner";
import ReactDiffViewer, { DiffMethod } from "react-diff-viewer-continued";
import { diffLines } from "diff";

const ANNOTATABLE = new Set([".pdf", ".docx", ".doc", ".rtf", ".odt"]);
function isAnnotatable(filename) {
    if (!filename) return false;
    const i = filename.lastIndexOf(".");
    if (i < 0) return false;
    return ANNOTATABLE.has(filename.slice(i).toLowerCase());
}

export default function DevelopmentTab({ project, statuses, onProjectChange }) {
    const [versions, setVersions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [uploadOpen, setUploadOpen] = useState(false);
    const [uploadPrefill, setUploadPrefill] = useState(null); // {label, notes} to prefill the dialog
    const [emailFor, setEmailFor] = useState(null); // version object
    const [compareOpen, setCompareOpen] = useState(false);

    const fetchVersions = async () => {
        setLoading(true);
        try {
            const rows = await api.get(`/projects/${project.id}/versions`).then((r) => r.data);
            setVersions(rows);
        } catch (e) {
            toast.error("Failed to load versions");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchVersions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [project.id]);

    const changeStatus = async (newStatus) => {
        try {
            const res = await api.patch(`/projects/${project.id}`, { status: newStatus });
            onProjectChange(res.data);
            toast.success(`Status → ${newStatus}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to update status");
        }
    };

    const uploadFile = async ({ file, label, notes, parentVersionId }) => {
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("label", label || "");
            fd.append("notes", notes || "");
            if (parentVersionId) fd.append("parent_version_id", parentVersionId);
            const res = await api.post(`/projects/${project.id}/versions`, fd, {
                headers: { "Content-Type": undefined },
            });
            toast.success(`Uploaded v${res.data.version_number}`);
            setUploadOpen(false);
            setUploadPrefill(null);
            fetchVersions();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Upload failed");
        } finally {
            setUploading(false);
        }
    };

    const openReupload = (v) => {
        setUploadPrefill({
            label: v.label || `Draft v${v.version_number}`,
            notes: v.notes || "",
        });
        setUploadOpen(true);
    };

    const deleteVersion = async (v) => {
        if (!confirm(`Delete ${v.label || `v${v.version_number}`}? This can't be undone.`)) return;
        try {
            await api.delete(`/versions/${v.id}`);
            fetchVersions();
        } catch (e) {
            toast.error("Delete failed");
        }
    };

    return (
        <div>
            {/* Status bar */}
            <div className="paper border border-border rounded-sm p-5 mb-8 flex items-center gap-6 flex-wrap" data-testid="dev-status-bar">
                <div>
                    <div className="label-mono">Development status</div>
                    <div className="mt-1">
                        <StatusPipeline current={project.status || "Editorial"} onChange={changeStatus} statuses={statuses} />
                    </div>
                </div>
                <div className="grow" />
                {versions.length >= 2 && (
                    <button
                        className="btn-ghost"
                        onClick={() => setCompareOpen(true)}
                        data-testid="compare-versions-btn"
                    >
                        <GitCompare className="w-4 h-4" strokeWidth={1.5} /> Compare drafts
                    </button>
                )}
                <button
                    className="btn-primary"
                    onClick={() => setUploadOpen(true)}
                    data-testid="upload-version-btn"
                >
                    <Upload className="w-4 h-4" strokeWidth={1.5} /> Upload draft
                </button>
            </div>

            {/* Versions list */}
            {loading ? (
                <div className="text-sm font-mono text-muted-foreground pulse-mono">Loading versions…</div>
            ) : versions.length === 0 ? (
                <div className="paper border border-border rounded-sm p-10 text-center" data-testid="dev-empty">
                    <Layers className="w-6 h-6 mx-auto mb-3 text-muted-foreground" strokeWidth={1.5} />
                    <h3 className="font-serif text-2xl">No drafts yet</h3>
                    <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
                        Upload your first draft (Word, PDF, or picture-book dummy pages).
                        Each upload becomes a numbered version so you can see how the project evolves.
                    </p>
                </div>
            ) : (
                <div className="space-y-4" data-testid="versions-list">
                    {versions.map((v) => (
                        <VersionCard
                            key={v.id}
                            version={v}
                            versions={versions}
                            onSaved={fetchVersions}
                            onDelete={() => deleteVersion(v)}
                            onEmail={() => setEmailFor(v)}
                            onReupload={() => openReupload(v)}
                            onMarkupUpload={(file) =>
                                uploadFile({
                                    file,
                                    label: "",  // let backend derive "Markup of ..."
                                    notes: `Marked-up copy of v${v.version_number}`,
                                    parentVersionId: v.id,
                                })
                            }
                        />
                    ))}
                </div>
            )}

            {uploadOpen && (
                <UploadDialog
                    nextVersion={(versions[0]?.version_number || 0) + 1}
                    busy={uploading}
                    prefill={uploadPrefill}
                    onClose={() => { setUploadOpen(false); setUploadPrefill(null); }}
                    onSubmit={uploadFile}
                />
            )}
            {emailFor && (
                <EmailVersionDialog
                    project={project}
                    version={emailFor}
                    onClose={() => setEmailFor(null)}
                />
            )}
            {compareOpen && versions.length >= 2 && (
                <CompareDialog
                    project={project}
                    versions={versions}
                    onClose={() => setCompareOpen(false)}
                />
            )}
        </div>
    );
}

function StatusPipeline({ current, onChange, statuses }) {
    const list = statuses && statuses.length ? statuses : [
        "Editorial", "Art Direction", "Revisions", "Ready to Submit", "Submitted", "Sold",
    ];
    return (
        <div className="flex flex-wrap gap-1 items-center" data-testid="status-pipeline">
            {list.map((s, i) => {
                const isCurrent = s === current;
                return (
                    <React.Fragment key={s}>
                        <button
                            onClick={() => onChange(s)}
                            data-testid={`status-btn-${s}`}
                            className={
                                "px-3 py-1 text-xs font-mono uppercase tracking-widest rounded-sm border transition-colors " +
                                (isCurrent
                                    ? "bg-primary text-primary-foreground border-primary"
                                    : "bg-white border-border text-muted-foreground hover:text-foreground hover:border-foreground/40")
                            }
                        >
                            {s}
                        </button>
                        {i < list.length - 1 && (
                            <span className="text-muted-foreground/40 text-xs">›</span>
                        )}
                    </React.Fragment>
                );
            })}
        </div>
    );
}

function VersionCard({ version, versions, onSaved, onDelete, onEmail, onReupload, onMarkupUpload }) {
    const [editing, setEditing] = useState(false);
    const [label, setLabel] = useState(version.label || "");
    const [notes, setNotes] = useState(version.notes || "");
    const [saving, setSaving] = useState(false);
    const [uploadingMarkup, setUploadingMarkup] = useState(false);
    const markupInputRef = useRef(null);

    const fileMissing = !!version.file_missing;
    // Find the parent version if this one was uploaded as a markup of another
    const parent = version.parent_version_id
        ? (versions || []).find((v) => v.id === version.parent_version_id)
        : null;

    const save = async () => {
        setSaving(true);
        try {
            await api.patch(`/versions/${version.id}`, { label, notes });
            toast.success("Version updated");
            setEditing(false);
            onSaved();
        } catch (e) {
            toast.error("Failed to save");
        } finally {
            setSaving(false);
        }
    };

    const downloadUrl = `${API}/versions/${version.id}/download`;
    // The /pdf endpoint serves PDFs inline (Content-Disposition: inline) so
    // opening it in a new tab shows the browser's native PDF viewer. On Safari
    // (Mac + iPad) that includes full markup tools (pen, highlight, text) with
    // Apple Pencil support. Users can then ⌘S to save the marked-up copy and
    // re-upload it via the "Upload markup" button below.
    const inlinePdfUrl = `${API}/versions/${version.id}/pdf`;

    const onMarkupFile = async (e) => {
        const f = e.target.files?.[0];
        e.target.value = "";
        if (!f) return;
        setUploadingMarkup(true);
        try {
            await onMarkupUpload(f);
        } finally {
            setUploadingMarkup(false);
        }
    };

    return (
        <div
            className={
                "paper border rounded-sm p-5 " +
                (fileMissing ? "border-[#8C2D19]/40 bg-[#8C2D19]/[0.03]" : "border-border")
            }
            data-testid={`version-card-${version.id}`}
        >
            {fileMissing && (
                <div
                    className="mb-4 -mx-5 -mt-5 px-5 py-3 bg-[#8C2D19]/10 border-b border-[#8C2D19]/30 flex items-center justify-between gap-3 flex-wrap"
                    data-testid={`version-file-lost-${version.id}`}
                >
                    <div className="text-sm">
                        <span className="font-semibold text-[#8C2D19]">File lost.</span>{" "}
                        <span className="text-foreground/80">
                            The uploaded file for this version isn&apos;t available on the server anymore
                            (this can happen after a redeploy on older records). Re-upload it to restore
                            annotation, download, and email actions.
                        </span>
                    </div>
                    <button
                        className="btn-primary text-xs shrink-0"
                        onClick={onReupload}
                        data-testid={`version-reupload-${version.id}`}
                        title="Upload a fresh copy to restore this version"
                    >
                        <Upload className="w-4 h-4" strokeWidth={1.5} /> Re-upload
                    </button>
                </div>
            )}
            <div className="flex items-start gap-4 flex-wrap">
                <div className="paper border border-border rounded-sm w-14 h-14 flex flex-col items-center justify-center shrink-0 bg-secondary">
                    <div className="label-mono">V</div>
                    <div className="font-serif text-2xl leading-none">{version.version_number}</div>
                </div>
                <div className="min-w-0 flex-1">
                    {editing ? (
                        <input
                            className="input-flat font-serif text-xl"
                            value={label}
                            onChange={(e) => setLabel(e.target.value)}
                            data-testid={`version-label-input-${version.id}`}
                        />
                    ) : (
                        <div className="font-serif text-xl">{version.label || `Draft v${version.version_number}`}</div>
                    )}
                    <div className="flex items-center gap-3 mt-1 flex-wrap">
                        <FileText className="w-3.5 h-3.5 text-muted-foreground" strokeWidth={1.5} />
                        <span className="font-mono text-xs text-muted-foreground truncate">{version.filename}</span>
                        <span className="label-mono">{(version.size / 1024).toFixed(0)} KB</span>
                        <span className="label-mono">{new Date(version.created_at).toLocaleDateString()}</span>
                        {parent && (
                            <span
                                className="inline-flex items-center gap-1 text-xs font-mono text-[#1B362F] bg-[#1B362F]/5 border border-[#1B362F]/20 rounded-sm px-2 py-0.5"
                                data-testid={`version-parent-link-${version.id}`}
                                title={`Marked-up version of ${parent.label || 'Draft v' + parent.version_number}`}
                            >
                                <PenLine className="w-3 h-3" strokeWidth={1.5} />
                                markup of {parent.label || `Draft v${parent.version_number}`}
                            </span>
                        )}
                    </div>
                </div>
                <div className="flex gap-2 shrink-0">
                    {isAnnotatable(version.filename) && !fileMissing && (
                        <Link
                            to={`/projects/${version.project_id}/versions/${version.id}/annotate`}
                            className="btn-primary text-xs"
                            data-testid={`version-annotate-${version.id}`}
                            title={version.filename.toLowerCase().endsWith('.pdf') ? "Annotate this PDF" : "Convert to PDF and annotate"}
                        >
                            <MessageSquare className="w-4 h-4" strokeWidth={1.5} /> Annotate
                        </Link>
                    )}
                    {!fileMissing && /\.pdf$/i.test(version.filename) && (
                        <a
                            href={inlinePdfUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="btn-ghost text-xs"
                            data-testid={`version-markup-${version.id}`}
                            title="Open the PDF in a new browser tab. On Safari (Mac/iPad) you can mark it up with pen and Apple Pencil, then save (⌘S) and upload the marked-up copy below."
                        >
                            <PenLine className="w-4 h-4" strokeWidth={1.5} /> Mark up
                        </a>
                    )}
                    {!fileMissing && (
                        <>
                            <button
                                className="btn-ghost text-xs disabled:opacity-60"
                                onClick={() => markupInputRef.current?.click()}
                                disabled={uploadingMarkup}
                                data-testid={`version-upload-markup-${version.id}`}
                                title="Upload the marked-up copy you saved from Preview/Pages — it will be filed as a new version linked to this one."
                            >
                                {uploadingMarkup ? (
                                    <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
                                ) : (
                                    <Upload className="w-4 h-4" strokeWidth={1.5} />
                                )}
                                {uploadingMarkup ? "Uploading…" : "Upload markup"}
                            </button>
                            <input
                                ref={markupInputRef}
                                type="file"
                                accept=".pdf,.docx,.doc"
                                className="hidden"
                                onChange={onMarkupFile}
                                data-testid={`version-upload-markup-input-${version.id}`}
                            />
                        </>
                    )}
                    {!fileMissing && (
                        <a
                            href={downloadUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="btn-ghost text-xs"
                            data-testid={`version-download-${version.id}`}
                        >
                            <Download className="w-4 h-4" strokeWidth={1.5} /> Download
                        </a>
                    )}
                    {!fileMissing && (
                        <button className="btn-ghost text-xs" onClick={onEmail} data-testid={`version-email-${version.id}`}>
                            <Mail className="w-4 h-4" strokeWidth={1.5} /> Email
                        </button>
                    )}
                    {!editing ? (
                        <button
                            className="btn-ghost text-xs"
                            onClick={() => setEditing(true)}
                            data-testid={`version-edit-${version.id}`}
                        >
                            <Pencil className="w-4 h-4" strokeWidth={1.5} /> Edit
                        </button>
                    ) : (
                        <button
                            className="btn-primary text-xs"
                            onClick={save}
                            disabled={saving}
                            data-testid={`version-save-${version.id}`}
                        >
                            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" strokeWidth={1.5} />}
                            Save
                        </button>
                    )}
                    <button
                        className="btn-ghost text-xs text-[#8C2D19]"
                        onClick={onDelete}
                        data-testid={`version-delete-${version.id}`}
                    >
                        <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                </div>
            </div>

            {editing ? (
                <div className="mt-4">
                    <div className="label-mono mb-1">Notes</div>
                    <textarea
                        className="input-flat min-h-[100px]"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Editorial notes, art direction, changes since previous version…"
                        data-testid={`version-notes-input-${version.id}`}
                    />
                </div>
            ) : (
                version.notes && (
                    <div className="mt-4 border-t border-border pt-4">
                        <div className="label-mono mb-1">Notes</div>
                        <p className="text-sm leading-relaxed whitespace-pre-wrap">{version.notes}</p>
                    </div>
                )
            )}
        </div>
    );
}

function UploadDialog({ nextVersion, busy, prefill, onClose, onSubmit }) {
    const [file, setFile] = useState(null);
    const [label, setLabel] = useState(prefill?.label || `Draft v${nextVersion}`);
    const [notes, setNotes] = useState(prefill?.notes || "");
    const fileRef = useRef(null);
    const isReupload = !!prefill;

    const submit = () => {
        if (!file) return toast.error("Choose a file");
        onSubmit({ file, label, notes });
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="upload-version-dialog">
            <div className="paper border border-border rounded-sm w-full max-w-lg bg-background p-8">
                <div className="label-mono mb-2">{isReupload ? "Re-upload" : "Upload"}</div>
                <h3 className={"font-serif text-3xl " + (isReupload ? "mb-2" : "mb-6")}>
                    {isReupload ? `Restore "${prefill.label}"` : `Add draft v${nextVersion}`}
                </h3>
                {isReupload && (
                    <p className="text-sm text-muted-foreground mb-6">
                        This will save as v{nextVersion}. You can delete the broken older record afterwards.
                    </p>
                )}

                <label className="block mb-4">
                    <span className="label-mono">File</span>
                    <input
                        ref={fileRef}
                        type="file"
                        accept=".pdf,.docx,.doc,.txt,.md,.jpg,.jpeg,.png,.webp,.gif"
                        onChange={(e) => setFile(e.target.files?.[0] || null)}
                        className="mt-2 block w-full text-sm file:mr-3 file:py-2 file:px-4 file:border-0 file:bg-primary file:text-primary-foreground file:rounded-sm hover:file:bg-[#12241F]"
                        data-testid="upload-version-file"
                    />
                </label>
                <label className="block mb-4">
                    <span className="label-mono">Label</span>
                    <input
                        className="input-flat mt-2"
                        value={label}
                        onChange={(e) => setLabel(e.target.value)}
                        placeholder="e.g. 'Draft 2 — after art direction'"
                        data-testid="upload-version-label"
                    />
                </label>
                <label className="block mb-6">
                    <span className="label-mono">Notes (optional)</span>
                    <textarea
                        className="input-flat mt-2 min-h-[100px]"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="What changed in this version, editorial direction, todos…"
                        data-testid="upload-version-notes"
                    />
                </label>

                <div className="flex gap-2 justify-end">
                    <button className="btn-ghost" onClick={onClose} data-testid="upload-version-cancel">Cancel</button>
                    <button
                        className="btn-primary disabled:opacity-60"
                        onClick={submit}
                        disabled={busy || !file}
                        data-testid="upload-version-submit"
                    >
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" strokeWidth={1.5} />}
                        {busy ? "Uploading…" : "Upload"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function EmailVersionDialog({ project, version, onClose }) {
    const [toName, setToName] = useState(project.author || "");
    const [toEmail, setToEmail] = useState(project.author_email || "");
    const [subject, setSubject] = useState(`${project.title} — ${version.label || `Draft v${version.version_number}`}`);
    const [message, setMessage] = useState("");
    const [busy, setBusy] = useState(false);

    const send = async () => {
        if (!toEmail.trim() || !toEmail.includes("@")) return toast.error("Enter a valid author email");
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("to_email", toEmail.trim());
            fd.append("to_name", toName);
            fd.append("subject", subject);
            fd.append("message", message);
            const res = await api.post(`/versions/${version.id}/email-to-author`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success(`Sent to ${res.data.to}`);
            onClose();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Send failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="email-version-dialog">
            <div className="paper border border-border rounded-sm w-full max-w-lg bg-background p-8">
                <div className="label-mono mb-2">Email draft</div>
                <h3 className="font-serif text-3xl mb-1">Send {version.label || `v${version.version_number}`} to author</h3>
                <p className="text-sm text-muted-foreground mb-6">
                    <Chip>v{version.version_number}</Chip>
                    <span className="ml-2">{version.filename}</span>
                </p>

                <div className="space-y-4">
                    <label className="block">
                        <span className="label-mono">Author name</span>
                        <input className="input-flat mt-1" value={toName} onChange={(e) => setToName(e.target.value)} data-testid="email-v-name" />
                    </label>
                    <label className="block">
                        <span className="label-mono">Author email *</span>
                        <input className="input-flat mt-1 font-mono" value={toEmail} onChange={(e) => setToEmail(e.target.value)} placeholder="author@example.com" data-testid="email-v-email" />
                    </label>
                    <label className="block">
                        <span className="label-mono">Subject</span>
                        <input className="input-flat mt-1" value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="email-v-subject" />
                    </label>
                    <label className="block">
                        <span className="label-mono">Note (optional)</span>
                        <textarea
                            className="input-flat mt-1 min-h-[100px]"
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            placeholder="Leave blank for a default note. The version's own notes will be appended below."
                            data-testid="email-v-message"
                        />
                    </label>
                </div>

                <div className="flex gap-2 justify-end mt-6">
                    <button className="btn-ghost" onClick={onClose} data-testid="email-v-cancel">Cancel</button>
                    <button className="btn-primary disabled:opacity-60" onClick={send} disabled={busy} data-testid="email-v-submit">
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" strokeWidth={1.5} />}
                        {busy ? "Sending…" : "Send draft"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function CompareDialog({ project, versions, onClose }) {
    // Default to newest (index 0) vs previous (index 1)
    const [aId, setAId] = useState(versions[1]?.id || versions[0]?.id || "");
    const [bId, setBId] = useState(versions[0]?.id || "");
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [splitView, setSplitView] = useState(true);

    const load = async () => {
        if (!aId || !bId || aId === bId) {
            setData(null);
            return;
        }
        setLoading(true);
        try {
            const res = await api
                .get(`/projects/${project.id}/versions/compare`, { params: { a: aId, b: bId } })
                .then((r) => r.data);
            setData(res);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load diff");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [aId, bId]);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="compare-dialog">
            <div className="paper border border-border rounded-sm w-full max-w-[1200px] max-h-[92vh] bg-background flex flex-col">
                {/* Header */}
                <div className="p-6 border-b border-border flex items-start justify-between gap-4 flex-wrap">
                    <div>
                        <div className="label-mono mb-1">Compare Drafts</div>
                        <h3 className="font-serif text-3xl">{project.title}</h3>
                        <p className="text-xs text-muted-foreground mt-1">
                            Text extracted from Word / PDF / TXT drafts. Image drafts can&apos;t be diffed as text.
                        </p>
                    </div>
                    <button className="btn-ghost" onClick={onClose} data-testid="compare-close">
                        <X className="w-4 h-4" strokeWidth={1.5} /> Close
                    </button>
                </div>

                {/* Controls */}
                <div className="p-6 border-b border-border grid grid-cols-1 md:grid-cols-[1fr_auto_1fr_auto_auto] gap-3 items-end">
                    <VersionPicker
                        label="Left (A)"
                        versions={versions}
                        value={aId}
                        onChange={setAId}
                        testId="compare-a"
                    />
                    <div className="hidden md:block pb-2 text-muted-foreground text-lg">→</div>
                    <VersionPicker
                        label="Right (B)"
                        versions={versions}
                        value={bId}
                        onChange={setBId}
                        testId="compare-b"
                    />
                    <label className="inline-flex items-center gap-2 text-xs pb-2">
                        <input
                            type="checkbox"
                            checked={splitView}
                            onChange={(e) => setSplitView(e.target.checked)}
                            data-testid="compare-split-toggle"
                        />
                        Split view
                    </label>
                    <button
                        className="btn-ghost disabled:opacity-60"
                        disabled={!data || !!data?.a?.error || !!data?.b?.error}
                        onClick={async () => {
                            if (!data) return;
                            const text = buildChangeList({
                                project,
                                a: data.a,
                                b: data.b,
                            });
                            try {
                                await navigator.clipboard.writeText(text);
                                toast.success("Change list copied to clipboard");
                            } catch {
                                toast.error("Copy failed — your browser may block clipboard access");
                            }
                        }}
                        data-testid="compare-copy-changes"
                        title="Copy a plain-text change list of what changed between A and B"
                    >
                        <ClipboardCopy className="w-4 h-4" strokeWidth={1.5} /> Copy changes
                    </button>
                </div>

                {/* Diff */}
                <div className="flex-1 overflow-auto p-6 min-h-0 bg-[#FBFAF6]" data-testid="compare-diff">
                    {aId === bId ? (
                        <div className="text-sm text-muted-foreground text-center py-16">
                            Choose two <em>different</em> versions to compare.
                        </div>
                    ) : loading ? (
                        <div className="pulse-mono font-mono text-sm text-muted-foreground text-center py-16">
                            Extracting text and computing diff…
                        </div>
                    ) : !data ? null : data.a.error || data.b.error ? (
                        <div className="paper border border-border rounded-sm p-6 text-sm">
                            <div className="text-[#8C2D19] font-medium mb-2">Can&apos;t compare these two.</div>
                            {data.a.error && <div>Left: {data.a.error}</div>}
                            {data.b.error && <div>Right: {data.b.error}</div>}
                            <div className="text-muted-foreground mt-2 text-xs">
                                Only text-based files (.docx, .pdf, .txt, .md) can be diffed. Picture-book dummy pages
                                are image files — you can still download and email them, but not diff them as text.
                            </div>
                        </div>
                    ) : (
                        <div className="diff-scope">
                            <ReactDiffViewer
                                oldValue={data.a.text}
                                newValue={data.b.text}
                                splitView={splitView}
                                compareMethod={DiffMethod.WORDS}
                                leftTitle={data.a.label}
                                rightTitle={data.b.label}
                                useDarkTheme={false}
                                styles={diffStyles}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function VersionPicker({ label, versions, value, onChange, testId }) {
    return (
        <label className="block">
            <span className="label-mono">{label}</span>
            <select
                className="input-flat mt-1"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                data-testid={testId}
            >
                {versions.map((v) => (
                    <option key={v.id} value={v.id}>
                        v{v.version_number} · {v.label || `Draft v${v.version_number}`}
                    </option>
                ))}
            </select>
        </label>
    );
}

const diffStyles = {
    variables: {
        light: {
            diffViewerBackground: "#FBFAF6",
            diffViewerColor: "#1C1917",
            addedBackground: "#DFE9D8",
            addedColor: "#12241F",
            removedBackground: "#F3D6CD",
            removedColor: "#5C1B0E",
            wordAddedBackground: "#B8D2A6",
            wordRemovedBackground: "#E6B5A6",
            addedGutterBackground: "#CCE0BF",
            removedGutterBackground: "#EBC3B4",
            gutterBackground: "#F0EBDE",
            gutterBackgroundDark: "#E8E2D9",
            highlightBackground: "#F4F1EB",
            highlightGutterBackground: "#E8E2D9",
            codeFoldGutterBackground: "#E8E2D9",
            codeFoldBackground: "#F4F1EB",
            emptyLineBackground: "#F7F4EC",
            gutterColor: "#5C574F",
            addedGutterColor: "#12241F",
            removedGutterColor: "#5C1B0E",
            codeFoldContentColor: "#5C574F",
            diffViewerTitleBackground: "#1B362F",
            diffViewerTitleColor: "#F4F1EB",
            diffViewerTitleBorderColor: "#12241F",
        },
    },
    contentText: {
        fontFamily:
            "'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
        fontSize: 12.5,
        lineHeight: "1.55",
    },
    titleBlock: {
        fontFamily: "'Manrope', sans-serif",
        fontSize: 12,
        letterSpacing: "0.16em",
        textTransform: "uppercase",
        padding: "10px 16px",
    },
};


// -----------------------------------------------------------------------------
// Build a plain-text change list from two text bodies.
// -----------------------------------------------------------------------------
function buildChangeList({ project, a, b }) {
    const oldText = (a.text || "").replace(/\r\n/g, "\n");
    const newText = (b.text || "").replace(/\r\n/g, "\n");

    // Get raw diff parts.
    const parts = diffLines(oldText, newText, { newlineIsToken: false });

    // Walk parts and coalesce a "removed → added" pair into a "changed" bucket.
    const added = [];   // list of trimmed paragraphs
    const removed = []; // list of trimmed paragraphs
    const changed = []; // list of { from, to }

    const splitParagraphs = (s) =>
        s.split(/\n+/).map((x) => x.trim()).filter(Boolean);

    for (let i = 0; i < parts.length; i++) {
        const p = parts[i];
        const next = parts[i + 1];
        if (p.removed && next && next.added) {
            // Pair up removed → added as edits
            const rems = splitParagraphs(p.value);
            const adds = splitParagraphs(next.value);
            const n = Math.max(rems.length, adds.length);
            for (let j = 0; j < n; j++) {
                if (rems[j] && adds[j]) {
                    changed.push({ from: rems[j], to: adds[j] });
                } else if (rems[j]) {
                    removed.push(rems[j]);
                } else if (adds[j]) {
                    added.push(adds[j]);
                }
            }
            i += 1; // skip the paired "added" part
        } else if (p.added) {
            added.push(...splitParagraphs(p.value));
        } else if (p.removed) {
            removed.push(...splitParagraphs(p.value));
        }
        // Unchanged blocks are ignored in the summary.
    }

    // Cap ridiculously long single lines for readability
    const cap = (s, n = 260) => (s.length > n ? s.slice(0, n - 1).trimEnd() + "…" : s);

    const now = new Date().toLocaleDateString(undefined, {
        year: "numeric", month: "long", day: "numeric",
    });

    const header = [
        `Change list · ${project.title || "Untitled"}`,
        `${a.label} → ${b.label}`,
        `Generated ${now}`,
        `Summary: ${added.length} added, ${removed.length} removed, ${changed.length} changed`,
    ].join("\n");

    const sections = [];

    if (added.length) {
        sections.push(
            "ADDED",
            ...added.map((s) => `  • ${cap(s)}`),
        );
    }
    if (removed.length) {
        sections.push(
            "REMOVED",
            ...removed.map((s) => `  • ${cap(s)}`),
        );
    }
    if (changed.length) {
        sections.push("CHANGED");
        changed.forEach(({ from, to }) => {
            sections.push(`  • FROM: ${cap(from)}`);
            sections.push(`    TO:   ${cap(to)}`);
        });
    }

    if (!sections.length) {
        sections.push("(No textual differences detected.)");
    }

    return [header, "", ...sections].join("\n") + "\n";
}

